import React from "react";
import configImg from "../../images/wc-config.jpeg"; // Adjust path as needed
import outputImg from "../../images/wc-output.jpeg"; // Adjust path as needed

export default function WooCommerce() {
    const handleScrollTo = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <>
            <div className="grid grid-cols-12 gap-1" id="Introduction">
                <div className="col-span-12 md:col-span-8 gap-1">
                    {/* Title */}
                    <div>
                        <p className="font-bold text-[32px]">Integrate BenePay with WooCommerce</p>
                    </div>

                    <div>
                        <hr className="border-t border-gray-300 my-2 w-full" />
                    </div>

                    {/* Overview */}
                    <div>
                        <div className="col-span-12 md:col-span-8 gap-1">
                            <div>
                                <p className="text-gray-700 mb-4">
                                    This guide provides comprehensive instructions for installing and configuring the BenePay Payment plugin for WooCommerce stores. By integrating BenePay, merchants can effortlessly enhance the payment processing capabilities of their WooCommerce stores—no coding required.
                                </p>
                                <div className="space-y-4 mt-6" id="Requirements">
                                    <div className="p-4 bg-gray-50 border-l-4 border-blue-500 rounded">
                                        <h3 className="font-semibold text-gray-800">Requirements</h3>
                                        <ul className="list-disc ml-5 mt-2 text-gray-600 space-y-2">
                                            <li>WordPress website with WooCommerce</li>
                                            <li>WordPress version at least 6.7</li>
                                            <li>WooCommerce version at least 9.5</li>
                                            <li>Administrative access to WordPress</li>
                                        </ul>
                                    </div>
                                    <div className="p-4 bg-gray-50 border-l-4 border-green-500 rounded" id="Onboarding">
                                        <h3 className="font-semibold text-gray-800">Onboarding Requirements</h3>
                                        <ul className="list-disc ml-5 mt-2 text-gray-600 space-y-2">
                                            <li>Merchant Name (Business Name)</li>
                                            <li>Email ID (Notification Purpose)</li>
                                            <li>Redirect URL (Hosted Domain with WooCommerce)</li>
                                            <li>Callback URL (Optional, see Callback Handling below)</li>
                                        </ul>
                                    </div>
                                    <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded" id="Onboarding">
                                        <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-yellow-500"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a1.25 1.25 0 001.08 1.88h18.2a1.25 1.25 0 001.08-1.88L13.71 3.86a1.25 1.25 0 00-2.42 0z" />
                                            </svg>

                                            Important Notes
                                        </h3>
                                        <ul className="list-disc ml-5 mt-2 text-gray-600 space-y-2">
                                            <li>Once the payment is successfully completed, our plugin updates the order status to “Processing”.</li>
                                            <li>If the payment fails, the order status is updated to “Failed”.</li>
                                            <li>
                                                For payments with a status of “In Process” or “Pending”, the plugin sets the order status to
                                                “On hold”. The callback mechanism will later update the order status once the final status is
                                                received from the payment gateway.
                                            </li>
                                            <li>
                                                Please note that only these statuses are updated by our plugin. We do not modify the order
                                                status under any other circumstances.
                                            </li>
                                            <li>
                                                For reference, we have provided the WooCommerce order status documentation link below.
                                            </li>
                                            <li>Order status managing - <a href="https://woocommerce.com/document/managing-orders/order-statuses/" className="text-blue-500">https://woocommerce.com/document/managing-orders/order-statuses/</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Plugin Download Link */}
                        <div className="flex justify-start mt-8">
                            <a
                                href="https://api-doc.benepay.io/plugins/benepay-payment-plugin-v1.5.zip" // Update this path to your actual plugin file location
                                download
                                className="inline-flex items-center px-5 py-2 bg-blue-600 text-white font-semibold rounded shadow hover:bg-blue-700 transition"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                                </svg>
                                Download BenePay WooCommerce Plugin - Version 1.5.0
                            </a>
                        </div>

                        {/* Installation Steps */}
                        <div className="mt-9" id="Installation">
                            <div>
                                <p className="text-gray-800 font-bold text-xl">Installation Steps</p>
                            </div>
                            <ol className="list-decimal ml-5 mt-3 text-gray-700 space-y-2">
                                <li>
                                    <span className="font-semibold">Download the Plugin:</span> Once onboarded with Benepay, save the above BenePay WooCommerce Plugin zip file to your computer.
                                </li>
                                <li>
                                    <span className="font-semibold">Install the Plugin:</span> Log in to your WordPress Admin Dashboard.<br />
                                    Navigate to <span className="font-mono">Plugins &rarr; Add New</span>.<br />
                                    Click <span className="font-mono">Upload Plugin</span>, choose the downloaded .zip file, and click <span className="font-mono">Install Now</span>.<br />
                                    Once installed, click <span className="font-mono">Activate</span>.
                                </li>
                                <li>
                                    <span className="font-semibold">Configure the Plugin:</span> Go to the <span className="font-semibold text-gray-500">BenePay Settings</span> menu in your WordPress admin panel.<br />
                                    Enter the credentials provided by Benepay during onboarding:
                                    <ul className="list-disc ml-8 mt-2 text-gray-600 space-y-1">
                                        <li>Encryption Key</li>
                                        <li>API URL</li>
                                        <li>X-API Key</li>
                                        <li>Auth URL</li>
                                        <li>Client ID</li>
                                        <li>Client Secret</li>
                                    </ul>
                                </li>
                                <li>
                                    <span className="font-semibold">Verify Installation:</span> Visit your WooCommerce checkout page. If BenePay appears as a payment method, installation is successful.
                                </li>
                            </ol>
                            
                            {/* Configuration & Output Images: Side by side on desktop, stacked on mobile */}
                            <div className="mt-6 flex flex-col md:flex-row md:gap-6">
                                <div className="flex-1 flex flex-col items-center mb-6 md:mb-0">
                                    <p className="text-gray-700 font-semibold mb-2">Plugin Configuration Example</p>
                                    <img
                                        src={configImg}
                                        alt="WooCommerce Plugin Configuration"
                                        className="rounded border shadow max-w-full"
                                        style={{ maxHeight: 350 }}
                                    />
                                </div>
                                <div className="flex-1 flex flex-col items-center">
                                    <p className="text-gray-700 font-semibold mb-2">Checkout Output Example</p>
                                    <img
                                        src={outputImg}
                                        alt="WooCommerce Checkout Output"
                                        className="rounded border shadow max-w-full"
                                        style={{ maxHeight: 350 }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Callback Handling */}
                        <div className="mt-9" id="Callback">
                            <div>
                                <p className="text-gray-800 font-bold text-xl">Callback Handling</p>
                            </div>
                            <div className="p-4">
                                {/* New explanatory content */}
                                <p className="text-gray-700 mb-4">
                                    <span className="font-semibold">Important:</span> Integrate the callback with your WordPress backend to handle and process the callback data posted from BenePay. This ensures your WooCommerce store can automatically update payment statuses, especially in cases where a payment is pending or if there is a service issue while the user is making a payment.
                                    <br /><br />
                                    Always follow the API security guidelines for decrypting the callback payload using your provided encryption key. Make sure callback handling is enabled on your end to receive real-time updates and maintain accurate order statuses.
                                </p>
                                <h3 className="font-semibold text-gray-800">Callback API</h3>
                                <p className="text-gray-600 mt-2">
                                    This is a Server-to-Server (S-to-S) API provided by BeneCollect. Merchants must implement this API to receive real-time updates on the status of their transactions.
                                </p>
                                <div className="mt-4">
                                    <p className="font-semibold text-gray-700">Sample Encrypted Response</p>
                                    <div className="overflow-x-auto bg-gray-900 text-white rounded-lg p-3 mt-2 text-xs">
                                        <pre className="whitespace-pre-wrap">
{`{
  encData: "=3xva/Nw5Xa93qXAvUHc0pUeyy3YtD2lZC8CdzFOdiw0RiFWnNN+cjO
yD2BxJ9NFU2wBihmRLGmZM/dDOBsbUeZ0sCH3HmOxIGxVtxKrohnhkn87YtXG
yG2We3EthntbL4cpqtVUh8wGk6qtg6Vf6dwvsLL+W+UUj0V4jNE7rPVcnISE5OrP
HcD1clDy7EdC2d09MtTM402CS1jUK1vJjIm9gG8m6fA3CycQtXCqQK1EJYR1yfcG
xzw6cg+mD07STcScxhB3PgFLubnn635pJHihMTcZ6QuzYhDEdnPxBhjivr6vIufJQ7
mYG39I46hcssYHdAZd9gbQTQcKEmqhlawdKg0m5coBth4uIOMUnNRM1WdIsa0
OOww3is7rvuAw4RVEixVAcMaMqqkmRnJ1eNyDF9anWsB6N1y9k6jSUvQXGxY1
cAADZYoCa0ISsnEJt6NhHdSRHQmw5pphYLUzdym+mZyhipKBvY4zEPSwhFNuSd
DCZNqpxUbXcw95jgHSCM7Lvcj2ddSw8QIgkX2cPqB2Jsrxn0/3roi6f7GSa7LCSky5
FrcZmC+sUvrUOLMQqaRKROytwrcsLPtYwGnH+gKE/WDjkNvP1LCWTWD/4dAsB
bbqftgxEQ3jknmCejd33kSSh0rKJoDEaiH3DtCO1W6VNgERqVbmSWEvKxEaHZsO
CyORkIpLBpWlHHlo8GAecSssDRXJDG9OtsviToeeVLtdeMXPn0E/PXO+YJbvYLM4
eBTWAws/bsmxUp+5n8+JeTnsXQWt9GjQGC4d2N8KlItu/haAeYEDakLlTmHAzpv
RMjEhQuYe+bEjyqGCKXMTnvAk0JqPdEwVEr8K0OPQ1YDjGnQ8i1Y/agfcy+6oKW
3nZKQKrE80VhF3LlRzbs9FDPIBQnMgrTQP8A6mwfsAk56hjquFlouAxYkoccvy/c+
+rgVSUe6Vg6QMf6WIL65ZseLD5+2J9jo70d/1k1R2ldQB/r4LzR5tGDdxrkUH5z6/1
07D1k064vqDpGp4Uu0pr"
}`}
                                        </pre>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <p className="text-gray-700">
                                        To retrieve payment information, decrypt the above using your <span className="font-mono">Encryption Key</span> provided by BenePay.
                                    </p>
                                </div>
                                <div className="mt-4">
                                    <p className="font-semibold text-gray-700">Sample Decrypted Response</p>
                                    <div className="overflow-x-auto bg-gray-900 text-white rounded-lg p-3 mt-2 text-xs">
                                        <pre className="whitespace-pre-wrap">
{`{
  "transactionId": "438f84efe1f156c0749452046",
  "requestorTransactionId": "0655097f-b6df-4984-96bf9c651fb32f8d",
  "payerName": "MyName",
  "payerEmail": "myemailid@benepay.io",
  "payerMobile": "+91-1234567890",
  "originalRequestedAmount": "124.56",
  "collectionCcy": "INR",
  "reasonForCollection": "OnlinePayment",
  "amountPaid": "124.56",
  "paymentTimestamp": 1733395780457,
  "outstandingAmount": null,
  "transactionStatus": "PAID",
  "message": "Transaction is Successful"
}`}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table of Contents */}
                <div className="hidden md:col-span-4 md:block">
                    <div className="sticky top-4 p-4">
                        <p className="text-gray-700 font-bold pl-5">TABLE OF CONTENTS</p>
                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("Introduction")}>Introduction</button>
                        </div>
                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("Requirements")}>Requirements</button>
                        </div>
                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("Onboarding")}>Onboarding</button>
                        </div>
                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("Installation")}>Installation Steps</button>
                        </div>
                        {/* <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("Callback")}>Callback Handling</button>
                        </div> */}
                    </div>
                </div>
            </div>
        </>
    );
}